import type { BackgroundJobsItem } from '../../@types/background-jobs';
import { recordApi } from '../../redux/services/record';
import {
  type AnalyzeRecordsBackgroundJobApiProps,
  JobStatus,
  setBackgroundJobDetails,
} from '../../redux/slices/background-job-slice';
import { appDispatch } from '../../redux/store-accessors';
import { BackgroundJobHandler } from './background-job.handler';
import { ExportRecordsBackgroundJobHandler } from './export-records.handler';

export class AnalyzeRecordsBackgroundJobHandler extends BackgroundJobHandler<AnalyzeRecordsBackgroundJobApiProps> {
  constructor(apiProps: AnalyzeRecordsBackgroundJobApiProps, details?: Record<string, unknown>) {
    super(apiProps, { ...details });
  }
  exportRecordHandler = new ExportRecordsBackgroundJobHandler(this.apiProps, this.details);

  public async createBackgroundJob() {
    const { moduleId, formId, extraPrompt } = this.apiProps;
    const exportRecordJobId = await this.exportRecordHandler.createBackgroundJob();

    const exportResult = await this.checkExportJobStatus(exportRecordJobId.backgroundJobId);
    if (!exportResult?.presignedUrl) {
      throw new Error('Export job failed');
    }

    const blob = (await this.fetchCSV(exportResult?.presignedUrl)) as Blob;

    const createJobRequest = appDispatch(
      recordApi.endpoints.startAnalysisJob.initiate({
        extraPrompt,
        body: { file: blob },
        moduleId: String(moduleId),
        formId,
      })
    );

    return createJobRequest.unwrap();
  }

  public getJobDetails(backgroundJobId: number) {
    const subscription = appDispatch(
      recordApi.endpoints.getAnalysisJobStatus.initiate(backgroundJobId, { forceRefetch: true })
    );
    return subscription.unwrap().finally(() => subscription.unsubscribe());
  }

  public successCallback({ result }: Record<string, string>): Promise<void> {
    appDispatch(
      setBackgroundJobDetails({
        id: String(this.details?.jobId ?? ''),
        details: { result },
      })
    );

    return Promise.resolve();
  }

  async fetchCSV(presignedUrl: string): Promise<Blob> {
    const response = await fetch(presignedUrl);
    const blob = await response.blob();

    return new Blob([blob], { type: 'text/csv' });
  }

  checkExportJobStatus = async (backgroundJobId: number): Promise<BackgroundJobsItem | undefined> => {
    const POLLING_TIME = 6000; // 6 seconds
    const MAX_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds
    const maxRetries = MAX_TIMEOUT / POLLING_TIME; // Calculate retries based on 20-minute timeout
    let attempts = 0;

    const handleFailure = () => {
      if (this.details?.jobId && typeof this.details.jobId === 'string') {
        const completedDate = new Date().toISOString();
        appDispatch(setBackgroundJobDetails({ id: this.details.jobId, status: JobStatus.Failed, completedDate }));
      }
    };

    while (attempts < maxRetries) {
      try {
        const jobDetails = await this.exportRecordHandler.getJobDetails(backgroundJobId);

        switch (jobDetails?.status) {
          case 'FINISHED':
            return jobDetails;
          case 'STAGED':
          case 'FAILED':
            handleFailure();
            return undefined;
        }
      } catch (_error) {
        handleFailure();
        return undefined;
      }

      attempts++;
      await new Promise((resolve) => setTimeout(resolve, POLLING_TIME));
    }
  };
}
